import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { OtpCodeField } from "@/components/auth/OtpCodeField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { AuthPageBrand } from "@/components/shared/AuthPageBrand";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { sanitizeEmailInput, sanitizeTextInput, stripControlCharsNoTrim } from "@/lib/security";
import { AUTH_LOGO_INLINE } from "@/lib/authLogoInline";

const readStoredMfaChallenge = () => {
  try {
    const raw = sessionStorage.getItem("voxara_pending_mfa");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.mfa_token) return null;
    return parsed;
  } catch (_error) {
    return null;
  }
};

const persistMfaChallenge = (payload) => {
  sessionStorage.setItem("voxara_pending_mfa", JSON.stringify(payload));
};

const clearMfaChallenge = () => {
  sessionStorage.removeItem("voxara_pending_mfa");
};

const LoginPage = () => {
  const storedMfaChallenge = readStoredMfaChallenge();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState(storedMfaChallenge?.mfa_token || "");
  const [mfaPin, setMfaPin] = useState("");
  const [pendingEmail, setPendingEmail] = useState(storedMfaChallenge?.email || "");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const mfaPinRef = useRef("");
  const loginInFlightRef = useRef(false);
  const verifyMfaInFlightRef = useRef(false);
  
  const { login, verifyMfa, resendMfa } = useAuth();
  const { language, t, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const getLoginErrorMessage = (error) => {
    const detail = error?.response?.data?.detail ?? error?.response?.data ?? null;

    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") return item.msg || item.message || null;
          return null;
        })
        .filter(Boolean);

      if (messages.length) {
        return messages.join(" · ");
      }
    }

    if (detail && typeof detail === "object") {
      return detail.msg || detail.message || (language === "es" ? "Datos de inicio de sesión inválidos." : "Invalid login data.");
    }

    if (error?.code === "ERR_NETWORK") {
      return language === "es"
        ? "No se pudo conectar con el servidor. Intenta de nuevo."
        : "Could not reach the server. Please try again.";
    }

    return language === "es" ? "Error al iniciar sesión" : "Login failed. Please try again.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || loginInFlightRef.current) {
      return;
    }
    loginInFlightRef.current = true;
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const submittedEmail = sanitizeEmailInput(String(formData.get("email") || email || ""));
    const submittedPassword = stripControlCharsNoTrim(String(formData.get("password") || password || "")).slice(0, 128);

    setEmail(submittedEmail);
    setPassword(submittedPassword);

    try {
      const result = await login(submittedEmail, submittedPassword);

      if (result.mfaRequired) {
        const challenge = {
          mfa_token: result.mfaToken,
          email: result.email || submittedEmail,
          is_admin: result.isAdmin || false,
          message: result.message || "",
        };
        persistMfaChallenge(challenge);
        setMfaToken(result.mfaToken);
        setPendingEmail(result.email || submittedEmail);
        setMfaPin("");
        toast.success(result.message || (language === "es" ? "Te enviamos un PIN por correo." : "We sent a PIN to your email."));
        return;
      }

      toast.success(language === "es" ? "¡Bienvenido!" : "Welcome back!");
      
      if (result.isAdmin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(String(getLoginErrorMessage(error)));
    } finally {
      loginInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleMfaPinChange = (nextPin) => {
    mfaPinRef.current = nextPin;
    setMfaPin(nextPin);
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (loading || verifyMfaInFlightRef.current) {
      return;
    }

    const stablePin = String(mfaPinRef.current || mfaPin || "").replace(/\D/g, "").slice(0, 6);
    if (stablePin.length !== 6) {
      toast.error(language === "es" ? "Ingresa el PIN de 6 dígitos" : "Enter the 6-digit PIN");
      return;
    }

    verifyMfaInFlightRef.current = true;
    setLoading(true);
    try {
      const result = await verifyMfa(mfaToken, stablePin);
      clearMfaChallenge();
      toast.success(language === "es" ? "Verificación completada" : "Verification completed");
      navigate(result.isAdmin ? "/admin" : "/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "es" ? "No se pudo verificar el PIN" : "Could not verify PIN"));
    } finally {
      verifyMfaInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleResendMfa = async () => {
    setLoading(true);
    try {
      await resendMfa(mfaToken);
      mfaPinRef.current = "";
      setMfaPin("");
      toast.success(language === "es" ? "Te enviamos un nuevo PIN" : "We sent you a new PIN");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (language === "es" ? "No se pudo reenviar el PIN" : "Could not resend the PIN"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    clearMfaChallenge();
    setMfaToken("");
    setMfaPin("");
    mfaPinRef.current = "";
    setPendingEmail("");
  };

  return (
    <div
      className="min-h-screen overflow-y-auto bg-background flex items-start md:items-center justify-center px-4 sm:px-6 py-8 md:py-10 hero-gradient"
      data-testid="login-page"
      lang={language}
      translate="no"
    >
      <AuthPageBrand testIdPrefix="login-page-brand" />

      {/* Language Toggle */}
      <div className="fixed top-4 right-4 flex items-center" data-testid="language-toggle">
        <button
          onClick={() => toggleLanguage("es")}
          className={`px-3 py-1.5 text-sm font-bold transition-all ${
            language === "es"
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="login-lang-es-btn"
        >
          ES
        </button>
        <button
          onClick={() => toggleLanguage("en")}
          className={`px-3 py-1.5 text-sm font-bold transition-all ${
            language === "en"
              ? "bg-primary text-primary-foreground"
              : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="login-lang-en-btn"
        >
          EN
        </button>
      </div>

      <Card className="notranslate w-full max-w-[30rem] md:w-[30rem] bg-card border-border p-7 md:p-8" translate="no">
        <div className="text-center mb-6 md:mb-7">
          <Link to="/" className="inline-flex flex-col items-center gap-3 mb-6" data-testid="login-card-logo-link">
            <img
              src={AUTH_LOGO_INLINE}
              alt="Voxara"
              className="h-28 sm:h-32 md:h-36 w-auto max-w-full object-contain"
              width="960"
              height="640"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
              data-testid="login-card-logo-image"
            />
          </Link>
          <h1 className="text-2xl font-bold">
            {mfaToken ? (language === "es" ? "Verifica tu ingreso" : "Verify your sign in") : t("welcomeBack")}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mfaToken
              ? (language === "es"
                  ? `Ingresa el PIN enviado a ${pendingEmail}`
                  : `Enter the PIN sent to ${pendingEmail}`)
              : t("signInAccess")}
          </p>
        </div>

        {!mfaToken ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                defaultValue={email}
                onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
                onInput={(e) => setEmail(sanitizeEmailInput(e.currentTarget.value))}
                required
                maxLength={254}
                autoComplete="email"
                className="bg-background border-border focus:border-primary"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("password")}</Label>
                <Link
                  to="/reset-password"
                  className="text-xs text-primary hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  {t("forgotPassword")}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  defaultValue={password}
                  onChange={(e) => setPassword(sanitizeTextInput(e.target.value, 128))}
                  onInput={(e) => setPassword(sanitizeTextInput(e.currentTarget.value, 128))}
                  required
                  maxLength={128}
                  autoComplete="current-password"
                  className="bg-background border-border focus:border-primary pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="login-password-visibility-button"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={loading}
              data-testid="login-submit-btn"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("signingIn")}
                </>
              ) : (
                t("signIn")
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyMfa} className="space-y-6">
            <div className="space-y-2">
              <Label>{language === "es" ? "PIN de verificación" : "Verification PIN"}</Label>
              <div className="flex justify-center">
                <OtpCodeField
                  value={mfaPin}
                  onChange={handleMfaPinChange}
                  testId="login-mfa-pin-input"
                  fallbackTestId="login-mfa-pin-input-fallback"
                  ariaLabel="Login verification PIN"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
              disabled={loading}
              data-testid="login-mfa-verify-button"
              translate="no"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === "es" ? "Verificando..." : "Verifying..."}
                </>
              ) : (
                language === "es" ? "Verificar PIN" : "Verify PIN"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResendMfa}
              disabled={loading}
              data-testid="login-mfa-resend-button"
              translate="no"
            >
              {language === "es" ? "Reenviar PIN" : "Resend PIN"}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleBackToLogin}
              disabled={loading}
              data-testid="login-mfa-back-button"
              translate="no"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === "es" ? "Volver" : "Back"}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {t("noAccount")}{" "}
          <Link
            to="/signup"
            className="text-primary hover:underline font-medium"
          >
            {t("signUp")}
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
